import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { CircularProgress, Box } from '@mui/material';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { isValidAdminUser } from './authConfig';
import { FEATURES, hasFeatureClaim } from './utils/constants';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Customers from './pages/Customers';
import CustomerDetails from './pages/CustomerDetails';
import PolicyGapReport from './pages/PolicyGapReport';
import Deployments from './pages/Deployments';
import Policies from './pages/Policies';
import PolicyMaintenance from './pages/PolicyMaintenance';
import FeatureCatalog from './pages/FeatureCatalog'; // This remains for managing feature keys
import PlanCatalogPage from './pages/PlanCatalogPage'; // New page for managing plans
import PlanMappingsPage from './pages/PlanMappingsPage'; // New page for mapping plans to features
import SystemHealth from './pages/SystemHealth';
import { MigrationUnauthorizedPage } from './features/migrations/pages/MigrationErrorPages';
import AdminLayout from './components/AdminLayout';
import ProtectedRoute from './components/ProtectedRoute';

// T2T Migrations Module — lazy loaded for code splitting
const MigrationsModule = lazy(() => import('./features/migrations/MigrationsModule'));

const migrationsRoute = {
  path: 'migrations/*',
  fbacRequired: FEATURES.T2T_MIGRATION,
} as const;

// Create Material-UI theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#667eea',
    },
    secondary: {
      main: '#764ba2',
    },
  },
});

function AuthChecker() {
  const isAuthenticated = useIsAuthenticated();
  const { accounts } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // If user is authenticated, validate they're from allowed domain
    if (isAuthenticated && accounts.length > 0) {
      const account = accounts[0];
      if (!isValidAdminUser(account.username, account.tenantId)) {
        // User authenticated but not from allowed domain
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        alert('Access denied. Admin portal requires a tc-config.com or technologyconsulting.llc email address.');
        navigate('/login');
      }
    }
  }, [isAuthenticated, accounts, navigate]);

  return null;
}

function App() {
  const { accounts } = useMsal();
  const canAccessMigrations = hasFeatureClaim(accounts[0], FEATURES.T2T_MIGRATION);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AuthChecker />
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          
          {/* Protected Routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="customers" element={<Customers />} />
            <Route path="customers/:id" element={<CustomerDetails />} />
            <Route path="customers/:id/gap-assessment" element={<PolicyGapReport />} />
            <Route path="deployments" element={<Deployments />} />
            <Route path="policies" element={<Policies />} />
            <Route path="policy-maintenance" element={<PolicyMaintenance />} />
            <Route path="feature-catalog" element={<FeatureCatalog />} />
            <Route path="plan-catalog" element={<PlanCatalogPage />} />
            <Route path="plan-mappings" element={<PlanMappingsPage />} />
            <Route path="system-health" element={<SystemHealth />} />
            <Route path="system-health" element={<div>System Health Page (Coming Soon)</div>} />
            <Route path="settings" element={<div>Settings Page (Coming Soon)</div>} />

            {/* T2T Migrations Module — code-split, lazy loaded */}
            <Route
              path={migrationsRoute.path}
              element={
                canAccessMigrations ? (
                  <Suspense fallback={<Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>}>
                    <MigrationsModule />
                  </Suspense>
                ) : (
                  <Navigate to="/not-authorized" replace state={{ requiredFeature: migrationsRoute.fbacRequired }} />
                )
              }
            />

            <Route path="not-authorized" element={<MigrationUnauthorizedPage />} />
          </Route>

          {/* Catch-all redirect to dashboard */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
